import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';

export type SuperchatPlacement = 'pre-login' | 'post-login' | 'always';

/**
 * Where a preview session records which revision it is looking at. Owned here
 * as well as by the widget, because ending a preview has to clear it wherever
 * the preview ends.
 */
export const PREVIEW_SESSION_KEY = 'pearsana.superchat.previewVersion';

export const clearPreviewMarker = () => {
	try {
		window.sessionStorage.removeItem(PREVIEW_SESSION_KEY);
	} catch {
		// Blocked storage had nothing to clear.
	}
};

export interface SuperchatConfig {
	applicationKey: string;
	placement: SuperchatPlacement;
	version: number;
}

export interface SuperchatRevision extends SuperchatConfig {
	updatedAt: string | null;
	updatedBy: string | null;
}

interface SuperchatState {
	config: SuperchatConfig | null;
	/** Set when the session is previewing an unpublished revision. */
	previewVersion: number | null;
	loading: boolean;
	loaded: boolean;
	revisions: SuperchatRevision[];
	saving: boolean;
	error: string | null;
}

const initialState: SuperchatState = {
	config: null,
	previewVersion: null,
	loading: false,
	loaded: false,
	revisions: [],
	saving: false,
	error: null
};

/**
 * Reads the published config. Runs on the guest path alongside fetchFeatureFlags,
 * because the widget can be asked to appear on the login screen where there is no
 * session to authenticate.
 */
export const fetchSuperchatConfig = createAsyncThunk<
	SuperchatConfig,
	void,
	{ dispatch: AppDispatch; state: RootState }
>('superchat/fetch', async () => {
	const response = await API.get('inspirusapi', '/superchat-config', {});
	return response as SuperchatConfig;
});

/**
 * Reads one revision, published or not. This is what makes preview session-scoped:
 * the previewing browser asks for a specific version, everyone else keeps reading
 * the live pointer.
 */
export const fetchSuperchatRevision = createAsyncThunk<
	SuperchatConfig,
	number,
	{ dispatch: AppDispatch; state: RootState }
>('superchat/fetchRevision', async (version, { rejectWithValue }) => {
	try {
		const response = await API.get('inspirusapi', `/superchat-config/revisions/${version}`, {});
		return response as SuperchatConfig;
	} catch (error) {
		// The marker outlives the page, so a revision that has gone would be asked
		// for again on every navigation — and the banner carrying "Exit preview"
		// is hidden the moment the preview ends, leaving no way out of the loop.
		// Clearing it here ends the preview properly rather than only visually.
		clearPreviewMarker();
		return rejectWithValue('That preview version is no longer available');
	}
});

/** Admin only. Every save creates a new version; nothing is overwritten. */
export const saveSuperchatConfig = createAsyncThunk<
	SuperchatConfig,
	{ applicationKey: string; placement: SuperchatPlacement },
	{ dispatch: AppDispatch; state: RootState; rejectValue: string }
>('superchat/save', async (body, { rejectWithValue }) => {
	try {
		const response = await API.put('inspirusapi', '/superchat-config', { body });
		return response as SuperchatConfig;
	} catch (error) {
		const details = (error as { response?: { data?: { details?: string[]; error?: string } } })?.response?.data;
		return rejectWithValue(details?.details?.join('. ') || details?.error || 'Could not save the configuration');
	}
});

export const fetchSuperchatRevisions = createAsyncThunk<
	SuperchatRevision[],
	void,
	{ dispatch: AppDispatch; state: RootState }
>('superchat/fetchRevisions', async () => {
	const response = await API.get('inspirusapi', '/superchat-config/revisions', {});
	return (response as { revisions: SuperchatRevision[] }).revisions;
});

const superchatSlice = createSlice({
	name: 'superchat',
	initialState,
	reducers: {
		/** Marks the session as previewing; the config itself arrives from fetchSuperchatRevision. */
		previewStarted(state, action: { payload: number }) {
			state.previewVersion = action.payload;
		},
		previewEnded(state) {
			state.previewVersion = null;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSuperchatConfig.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchSuperchatConfig.fulfilled, (state, action) => {
				state.loading = false;
				state.loaded = true;
				// A preview in progress outranks the published config: the whole point
				// is that this session sees something nobody else does.
				if (state.previewVersion === null) {
					state.config = action.payload;
				}
			})
			.addCase(fetchSuperchatConfig.rejected, (state) => {
				// A widget that cannot load its config is simply absent. It must never
				// be the reason a page fails to render.
				state.loading = false;
				state.loaded = true;
				state.config = null;
			})
			.addCase(fetchSuperchatRevision.fulfilled, (state, action) => {
				state.config = action.payload;
				state.loaded = true;
			})
			.addCase(fetchSuperchatRevision.rejected, (state) => {
				// The marker is cleared in the thunk, so the preview is over rather
				// than merely invisible: the next load reads the published config.
				state.previewVersion = null;
				state.loaded = true;
				state.config = null;
			})
			.addCase(saveSuperchatConfig.pending, (state) => {
				state.saving = true;
				state.error = null;
			})
			.addCase(saveSuperchatConfig.fulfilled, (state, action) => {
				state.saving = false;
				state.config = action.payload;
			})
			.addCase(saveSuperchatConfig.rejected, (state, action) => {
				state.saving = false;
				state.error = action.payload || 'Could not save the configuration';
			})
			.addCase(fetchSuperchatRevisions.fulfilled, (state, action) => {
				state.revisions = action.payload;
			});
	}
});

export const { previewStarted, previewEnded } = superchatSlice.actions;
export default superchatSlice.reducer;
